/**
 * Hook para cargar todos los consentimientos de un evento.
 * Útil para vistas de director/asociación que quieren ver el estado
 * de check-in de cada inscrito.
 */
import { useEffect, useState, useCallback } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { EventConsent, EventConsentChecklistConfig } from "@/types";
import {
  isConsentCompleted,
  buildChecklist,
} from "@/lib/services/eventConsents";

export interface ConsentProgress {
  consent: EventConsent | null;
  completed: boolean;
  doneCount: number;
  totalCount: number;
}

/** Suscripción en tiempo real a todos los consents de un evento. */
export function useEventConsents(
  eventId: string | undefined,
  checklistConfig?: EventConsentChecklistConfig,
) {
  const [consents, setConsents] = useState<EventConsent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setConsents([]);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, "eventConsents"),
      where("eventId", "==", eventId),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setConsents(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EventConsent),
        );
        setLoading(false);
      },
      (err) => {
        console.error("[useEventConsents] error", err);
        setError(err.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [eventId]);

  /** Index por memberId para lookup rápido. */
  const byMemberId = useCallback(
    (memberId: string | undefined): EventConsent | null => {
      if (!memberId) return null;
      return consents.find((c) => c.memberId === memberId) ?? null;
    },
    [consents],
  );

  /** Calcula el progreso (X/6 pasos). */
  const progressFor = useCallback(
    (memberId: string | undefined): ConsentProgress => {
      const consent = byMemberId(memberId);
      const checklist = buildChecklist(consent, checklistConfig);
      const required = checklist.filter((i) => i.required);
      return {
        consent,
        completed: isConsentCompleted(consent, checklistConfig),
        doneCount: required.filter((i) => i.done).length,
        totalCount: required.length,
      };
    },
    [byMemberId, checklistConfig],
  );

  return { consents, loading, error, byMemberId, progressFor };
}
